import React, { useEffect, useState } from "react";
import { Card, Empty, Field, StageChip } from "../../dashboard/primitives";
import {
  createPost,
  deletePost,
  setPostStatus,
  slugify,
  subscribeBlog,
  toDateInput,
  updatePost,
} from "../../blog/api";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const emptyDraft = {
  id: "",
  title: "",
  category: "",
  readTime: "",
  date: toDateInput(new Date()),
  excerpt: "",
  content: "",
  status: "draft",
};

const PostEditor = ({ draft, setDraft, onSubmit, onCancel, submitting, error, mode }) => {
  const change = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      style={{ display: "grid", gap: 14 }}
    >
      <Field label="Title">
        <input
          className="fw-input"
          value={draft.title}
          onChange={(e) => {
            const title = e.target.value;
            setDraft((d) => ({
              ...d,
              title,
              id: mode === "create" ? slugify(title) : d.id,
            }));
          }}
          required
        />
      </Field>

      <Field label={mode === "create" ? "Slug (auto from title, editable)" : "Slug (locked)"}>
        <input
          className="fw-input"
          value={draft.id}
          onChange={(e) => setDraft((d) => ({ ...d, id: slugify(e.target.value) }))}
          disabled={mode !== "create"}
          required
        />
      </Field>

      <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        <Field label="Category">
          <input className="fw-input" value={draft.category} onChange={change("category")} />
        </Field>
        <Field label="Read time">
          <input className="fw-input" placeholder="6 min read" value={draft.readTime} onChange={change("readTime")} />
        </Field>
        <Field label="Date">
          <input type="date" className="fw-input" value={draft.date} onChange={change("date")} />
        </Field>
      </div>

      <Field label="Excerpt">
        <textarea className="fw-textarea" rows={2} value={draft.excerpt} onChange={change("excerpt")} />
      </Field>

      <Field label="Content (markdown-ish: ## h2, ### h3, **bold**, blank line = paragraph)">
        <textarea className="fw-textarea" rows={14} value={draft.content} onChange={change("content")} />
      </Field>

      {error && <div style={{ color: "var(--fw-red)", fontSize: 13 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          disabled={submitting || !draft.id}
          onClick={() => onSubmit("draft")}
        >
          {submitting ? "Saving…" : "Save draft"}
        </button>
        <button
          type="button"
          className="fw-btn fw-btn--primary"
          disabled={submitting || !draft.id}
          onClick={() => onSubmit("live")}
        >
          {submitting ? "Saving…" : draft.status === "live" ? "Save & keep live" : "Publish live"}
        </button>
        {mode === "edit" && draft.status === "live" && (
          <button
            type="button"
            className="fw-btn fw-btn--ghost"
            disabled={submitting}
            onClick={() => onSubmit("unpublish")}
          >
            Unpublish
          </button>
        )}
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(
    () =>
      subscribeBlog((v) => {
        setPosts(v);
        setLoaded(true);
      }),
    []
  );

  const showSkeleton = useMinLoading(!loaded);

  const mode = editing === "new" ? "create" : editing ? "edit" : null;

  const startNew = () => {
    setError(null);
    setDraft(emptyDraft);
    setEditing("new");
  };

  const startEdit = (post) => {
    setError(null);
    setDraft({
      id: post.id,
      title: post.title || "",
      category: post.category || "",
      readTime: post.readTime || "",
      date: toDateInput(post.date) || toDateInput(new Date()),
      excerpt: post.excerpt || "",
      content: post.content || "",
      status: post.status === "live" ? "live" : "draft",
    });
    setEditing(post.id);
  };

  const save = async (intent) => {
    setError(null);
    setSubmitting(true);
    try {
      const nextStatus = intent === "live" ? "live" : intent === "unpublish" ? "draft" : "draft";
      const payload = { ...draft, status: nextStatus };
      if (mode === "create") {
        await createPost(payload);
      } else {
        const { id, ...patch } = payload;
        await updatePost(id, patch);
      }
      setEditing(null);
      setDraft(emptyDraft);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (post) => {
    const next = post.status === "live" ? "draft" : "live";
    await setPostStatus(post.id, next);
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    await deletePost(post.id);
    if (editing === post.id) setEditing(null);
  };

  const displayDate = (d) => {
    if (!d) return "—";
    const date = d.toDate ? d.toDate() : new Date(d);
    return isNaN(date) ? "—" : date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <>
      <Card
        title={editing ? (mode === "create" ? "New post" : `Editing · ${draft.title || draft.id}`) : "Blog posts"}
        action={
          editing ? null : (
            <button className="fw-btn fw-btn--primary" onClick={startNew}>New post</button>
          )
        }
      >
        {editing ? (
          <PostEditor
            draft={draft}
            setDraft={setDraft}
            onSubmit={save}
            onCancel={() => { setEditing(null); setError(null); }}
            submitting={submitting}
            error={error}
            mode={mode}
          />
        ) : showSkeleton ? (
          <SkeletonList count={4} ariaLabel="Loading posts">
            <SkeletonRow cols={5} widths={["1fr", "80px", "110px", "100px", "160px"]} />
          </SkeletonList>
        ) : posts.length === 0 ? (
          <Empty>No posts yet — publish your first.</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {posts.map((p) => {
              const isLive = p.status === "live";
              return (
                <div
                  key={p.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto auto auto",
                    gap: 12,
                    alignItems: "center",
                    padding: "14px 0",
                    borderBottom: "1px solid var(--fw-line)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.title || <em style={{ color: "var(--fw-muted)" }}>Untitled</em>}
                    </div>
                    <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)", marginTop: 2 }}>
                      /{p.id}
                    </div>
                  </div>
                  <StageChip status={isLive ? "paid" : "pending"} />
                  <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                    {displayDate(p.date)}
                  </span>
                  <button
                    className="fw-btn fw-btn--ghost"
                    style={{ padding: "6px 10px" }}
                    onClick={() => toggleStatus(p)}
                    title={isLive ? "Move back to draft" : "Publish live"}
                  >
                    {isLive ? "Unpublish" : "Publish"}
                  </button>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }} onClick={() => startEdit(p)}>Edit</button>
                    <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "var(--fw-red)", borderColor: "var(--fw-red)" }} onClick={() => remove(p)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default BlogSection;
